import { Controller } from "stimulus"
import { validate_email } from "./validate_controller"

export default class extends Controller {
    static targets = [
        "inputPaymentMethod",
        "extraFincimex",
        "extraCash",
        "extraTm",
        "extraEnz",
        "currencyCash",
        "noTransTm",
        "noTransEnz",
        "submitButton"
    ]

    // Los valores first-method y first-currency vienen como data attributes
    // del elemento raíz (data-payment-first-method-value, data-payment-first-currency-value)
    // Solo se usan informativamente en JS si fuera necesario; la lógica de
    // qué radios/divs existen ya la resuelve Rails en el ERB.

    connect() {
        this._hideAllExtras()
        const checked = this.inputPaymentMethodTargets.find(r => r.checked)
        if (checked) this._showExtra(checked.value)
        this.toggleButton()
    }

    // ─── Selección de método de pago ──────────────────────────────────────────

    selectPaymentMethod(event) {
        const value = event.target.value
        this._hideAllExtras()
        this._showExtra(value)
        this.toggleButton()
    }

    _hideAllExtras() {
        ;[
            this.hasExtraFincimexTarget && this.extraFincimexTarget,
            this.hasExtraCashTarget     && this.extraCashTarget,
            this.hasExtraTmTarget       && this.extraTmTarget,
            this.hasExtraEnzTarget      && this.extraEnzTarget,
        ].filter(Boolean).forEach(el => {
            el.classList.add("hide-tooltip");
            el.querySelectorAll("input, select, textarea").forEach(input => input.disabled = true);
        });
    }

    _showExtra(value) {
        const map = {
            fincimex: this.hasExtraFincimexTarget && this.extraFincimexTarget,
            cash:     this.hasExtraCashTarget     && this.extraCashTarget,
            tm:       this.hasExtraTmTarget       && this.extraTmTarget,
            enz:      this.hasExtraEnzTarget      && this.extraEnzTarget,
        };
        const target = map[value];
        if (target) {
            target.classList.remove("hide-tooltip");
            target.querySelectorAll("input, select, textarea").forEach(input => input.disabled = false);
        }
    }

    // ─── Validación inputs FINCIMEX / genéricos ───────────────────────────────

    validateInputPayment(event) {
        const input = event.target
        const ph    = input.placeholder.toLowerCase()
        let raw     = input.value

        // Correo (async)
        if (ph.includes("correo")) {
            validate_email(raw).then(ok => {
                this._markValidity(input, ok)
                this.toggleButton()
            })
            return
        }

        // Importe
        if (ph.includes("importe")) {
            // Aceptar coma o punto como separador decimal
            raw = raw.replace(/,/g, ".").slice(0, 10)
            const match = raw.match(/^\d*(?:\.\d{0,2})?$/)
            if (!match) raw = raw.slice(0, -1)
            input.value = raw

            const value    = parseFloat(raw)
            const maxAttr  = input.dataset.max ? parseFloat(input.dataset.max) : null
            const validFmt = raw !== "" && /^\d+(?:\.\d{1,2})?$/.test(raw)
            const validMin = !isNaN(value) && value > 0
            const validMax = maxAttr === null || (!isNaN(value) && value <= maxAttr)

            this._markValidity(input, validFmt && validMin && validMax)
            return this.toggleButton()
        }

        // Moneda (select)
        if (ph.includes("moneda") || input.name === "currency" || input.name === "fincimex_moneda" || input.name === "cash_moneda") {
            this._markValidity(input, !!input.value)
            return this.toggleButton()
        }

        // Fecha dd/mm/yyyy
        if (ph.includes("fecha")) {
            let d = raw.replace(/\D/g, "").slice(0, 8)
            if (d.length >= 1 && parseInt(d[0], 10) > 3)  d = d.slice(0, -1)
            if (d.length >= 2) { const day = parseInt(d.slice(0, 2), 10); if (day === 0 || day > 31) d = d.slice(0, -1) }
            if (d.length >= 3 && parseInt(d[2], 10) > 1)  d = d.slice(0, -1)
            if (d.length >= 4) { const month = parseInt(d.slice(2, 4), 10); if (month === 0 || month > 12) d = d.slice(0, -1) }

            let formatted = d
            if (d.length > 4)      formatted = d.slice(0, 2) + "/" + d.slice(2, 4) + "/" + d.slice(4)
            else if (d.length > 2) formatted = d.slice(0, 2) + "/" + d.slice(2)
            input.value = formatted

            this._updateMaskHint(input, formatted, "dd/mm/yyyy")

            let ok = false
            if (d.length === 8) {
                const day   = parseInt(d.slice(0, 2), 10)
                const month = parseInt(d.slice(2, 4), 10)
                const year  = parseInt(d.slice(4),    10)
                ok = this._isValidDateDMY(day, month, year)
            }
            this._markValidity(input, ok)
            return this.toggleButton()
        }

        // Hora hh:mm:ss
        if (ph.includes("hora")) {
            let t = raw.replace(/\D/g, "").slice(0, 6)
            if (t.length >= 1 && parseInt(t[0], 10) > 2) t = t.slice(0, -1)
            if (t.length >= 2) { const hh = parseInt(t.slice(0, 2), 10); if (hh > 23) t = t.slice(0, -1) }
            if (t.length >= 3 && parseInt(t[2], 10) > 5)  t = t.slice(0, -1)
            if (t.length >= 4) { const mm = parseInt(t.slice(2, 4), 10); if (mm > 59) t = t.slice(0, -1) }
            if (t.length >= 5 && parseInt(t[4], 10) > 5)  t = t.slice(0, -1)
            if (t.length >= 6) { const ss = parseInt(t.slice(4, 6), 10); if (ss > 59) t = t.slice(0, -1) }

            const isBlur = event.type === "blur"
            if (t.length === 4 && isBlur) t = t + "00"

            let formatted = t
            if (t.length > 4)      formatted = t.slice(0, 2) + ":" + t.slice(2, 4) + ":" + t.slice(4)
            else if (t.length > 2) formatted = t.slice(0, 2) + ":" + t.slice(2)
            if (input.value !== formatted) input.value = formatted

            this._updateMaskHint(input, formatted, "hh:mm:ss")
            this._markValidity(input, t.length === 6)
            return this.toggleButton()
        }

        // Genérico obligatorio (*)
        if (ph.trim().startsWith("*")) {
            this._markValidity(input, input.value.trim() !== "")
            return this.toggleButton()
        }

        // Opcional
        this._markValidity(input, true, true)
        this.toggleButton()
    }

    // ─── Validación TM / ENZ ─────────────────────────────────────────────────

    validateInputTmEnzPayment(event) {
        const input = event.target
        const ph    = input.placeholder.toLowerCase()

        if (ph.trim().startsWith("*")) {
            this._markValidity(input, input.value.trim() !== "")
            return this.toggleButton()
        }

        this._markValidity(input, true, true)
        this.toggleButton()
    }

    // ─── Habilitar / deshabilitar botón submit ────────────────────────────────

    toggleButton() {
        const paymentMethod = this.selectedRadioPaymentMethod

        const targetMap = {
            fincimex: this.hasExtraFincimexTarget && this.extraFincimexTarget,
            cash:     this.hasExtraCashTarget     && this.extraCashTarget,
            tm:       this.hasExtraTmTarget       && this.extraTmTarget,
            enz:      this.hasExtraEnzTarget      && this.extraEnzTarget,
        }

        const container = targetMap[paymentMethod]
        const requiredInputs = container
            ? Array.from(container.querySelectorAll("input, select")).filter(el =>
                el.placeholder?.trim().startsWith("*") || el.classList.contains("required")
            )
            : []

        // "pl" no tiene campos extra → el botón siempre está habilitado
        const hasInvalid = paymentMethod !== "pl" && requiredInputs.some(el =>
            !el.value.trim() || el.classList.contains("is-invalid")
        )

        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.classList.toggle("disabled", hasInvalid)
            this.submitButtonTarget.disabled = hasInvalid
        }
    }

    // ─── Prevenir doble submit ────────────────────────────────────────────────

    disableOnSubmit(event) {
        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = true
            this.submitButtonTarget.classList.add("disabled")
            this.submitButtonTarget.textContent = "Guardando..."
        }
    }

    // ─── Getter ───────────────────────────────────────────────────────────────

    get selectedRadioPaymentMethod() {
        const checked = this.inputPaymentMethodTargets.find(r => r.checked)
        return checked ? checked.value : null
    }

    // ─── Helpers privados ─────────────────────────────────────────────────────

    _markValidity(input, ok, optional = false) {
        if (ok) {
            input.classList.add("is-valid")
            input.classList.remove("is-invalid")
        } else {
            if (optional && input.value.trim() === "") {
                input.classList.remove("is-invalid", "is-valid")
                return
            }
            input.classList.add("is-invalid")
            input.classList.remove("is-valid")
        }
    }

    _updateMaskHint(input, value, mask) {
        const hint = input.parentElement?.querySelector(".input-mask-hint")
        if (!hint) return
        if (!value || value.length === 0) { hint.textContent = ""; return }
        hint.textContent = "\u00A0".repeat(value.length) + mask.slice(value.length)
    }

    _isValidDateDMY(d, m, y) {
        if (y < 1900 || y > 2100) return false
        const date = new Date(y, m - 1, d)
        return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d
    }
}